import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { Metamodel } from 'app/model/base/model';

import { CopySourceAdapter, commonCopySourceAdapts } from './copysource.adapter';
import { CopyActivity } from 'app/model/pipeline/copy.activity';
import { SparkCopySource } from 'app/model/pipeline/copysource/spark.copysource';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { GlobalVariables } from 'app/services/globals';
import moment from 'moment-timezone';
import { isLogicAppExpression } from 'app/shared/utils/expression.util';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(SparkCopySource),
    asynchronous: true,
    adapts: {
        ...commonCopySourceAdapts,
        query: '*',
        batchSize: GlobalVariables.enableSparkV2BatchSize ? '*' : undefined,
        queryTimeout: GlobalVariables.enableSparkV2QueryTimeout ? '*' : undefined
    }
})
export class SparkCopySourceAdapter extends CopySourceAdapter<SparkCopySource, Model.SparkSource> {

    protected override async _toResource(copySource: SparkCopySource): Promise<Model.SparkSource> {
        const result = await this._toTabularResource(copySource, Metamodel.modelFor(SparkCopySource).name);
        if (GlobalVariables.enableSparkV2BatchSize) {
            result.batchSize = ValueAdapter.toResource(copySource, SparkCopySource.BATCH_SIZE);
        }
        if (GlobalVariables.enableSparkV2QueryTimeout) {
            try {
                result.queryTimeout =
                    typeof copySource.queryTimeout === 'number'
                        ? moment.duration(copySource.queryTimeout, 'seconds').format('hh:mm:ss', { trim: false })
                        : ValueAdapter.toResource(copySource, SparkCopySource.QUERY_TIMEOUT);
            } catch {
                this._logError('Failed to convert queryTimeout into TimeSpan');
            }
        }
        return result;
    }

    protected override async _toModel(resource: Model.SparkSource, copyActivity: CopyActivity): Promise<SparkCopySource> {
        const result = (await this._toTabularModel(resource, copyActivity, SparkCopySource.MODEL)) as SparkCopySource;
        if (GlobalVariables.enableSparkV2BatchSize) {
            ValueAdapter.toModel(result, SparkCopySource.BATCH_SIZE, resource.batchSize);
        }
        if (GlobalVariables.enableSparkV2QueryTimeout) {
            try {
                if (resource.queryTimeout == null) {
                    result.queryTimeout = undefined;
                } else if (typeof resource.queryTimeout === 'string' && !isLogicAppExpression(resource.queryTimeout)) {
                    result.queryTimeout = moment.duration(resource.queryTimeout).asSeconds();
                } else {
                    ValueAdapter.toModel(result, SparkCopySource.QUERY_TIMEOUT, resource.queryTimeout);
                }
            } catch {
                this._logError('Failed to convert queryTimeout into number');
            }
        }
        return result;
    }
}
